import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import SocialMediaIcons from "../social-media-icons";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Users,
  CreditCard,
  Briefcase,
  UserCircle,
} from "lucide-react";

export function AgencyCard(props) {
  const {
    name,
    email,
    phoneNumber,
    country,
    currency,
    bankName,
    IBAN,
    lucaId,
    lucaAccountName,
    newTvAccountManager,
    customerCount,
    clientCount,
    socialMediaAccount,
    editClick,
    isHorizontal,
  } = props;

  const displayClientCount = customerCount || clientCount || 0;

  return (
    <Card
      className={cn(
        "group hover:shadow-xl transition-all duration-300 hover:border-primary/50 overflow-hidden",
        isHorizontal && "flex justify-between items-center"
      )}
    >
      {/* Header with gradient accent */}
      <div className="h-2 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500" />

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl mb-1 group-hover:text-primary transition-colors flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {name}
            </CardTitle>
            <CardDescription className="flex items-center gap-1.5 text-sm">
              <Mail className="h-3.5 w-3.5" />
              {email}
            </CardDescription>
          </div>
          {country && (
            <Badge variant="secondary" className="ml-2">
              <MapPin className="h-3 w-3 mr-1" />
              {country}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Social Media Accounts */}
        {socialMediaAccount && socialMediaAccount.length > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
            <SocialMediaIcons data={socialMediaAccount} />
          </div>
        )}

        {/* Contact & Business Info Grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {/* Client Count */}
          <div className="col-span-2 flex items-center gap-2 p-3 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800">
            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Clients</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {displayClientCount}
              </p>
            </div>
          </div>

          {phoneNumber && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4 text-primary" />
              <span className="truncate text-xs">{phoneNumber}</span>
            </div>
          )}

          {currency && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <CreditCard className="h-4 w-4 text-primary" />
              <span className="font-medium text-xs">{currency}</span>
            </div>
          )}

          {newTvAccountManager && (
            <div className="col-span-2 flex items-center gap-2 text-muted-foreground">
              <UserCircle className="h-4 w-4 text-primary" />
              <span className="truncate text-xs">{newTvAccountManager}</span>
            </div>
          )}
        </div>

        {/* Luca Info */}
        {(lucaId || lucaAccountName) && (
          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">
                Luca Account
              </span>
            </div>
            {lucaAccountName && (
              <p className="text-sm font-medium text-foreground mb-0.5">
                {lucaAccountName}
              </p>
            )}
            {lucaId && (
              <p className="text-xs text-muted-foreground font-mono">
                ID: {lucaId}
              </p>
            )}
          </div>
        )}

        {/* Bank Info */}
        {(bankName || IBAN) && (
          <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">
                Banking Details
              </span>
            </div>
            {bankName && (
              <p className="text-sm font-medium text-foreground mb-0.5">
                {bankName}
              </p>
            )}
            {IBAN && (
              <p className="text-xs text-muted-foreground font-mono truncate">
                {IBAN}
              </p>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-4 border-t bg-muted/20">
        <Button
          onClick={editClick}
          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
          variant="outline"
        >
          View Details →
        </Button>
      </CardFooter>
    </Card>
  );
}
